import { useEffect, useRef, useId } from "react";
import { supabase } from "@/lib/supabase";

/**
 * Invalidates the Account/Thread read model from durable P4 domain changes.
 * The callback is debounced because a single transition can update several
 * rows and append a domain event in the same transaction.
 */
export const useThreadStateRealtime = ({
  enabled,
  threadId,
  onRefresh,
}: {
  enabled: boolean;
  threadId?: string | null;
  onRefresh: () => void;
}) => {
  const subscriberId = useId();
  const refreshRef = useRef(onRefresh);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return undefined;

    const scheduleRefresh = () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        refreshRef.current();
      }, 180);
    };
    const threadFilter = threadId ? `thread_id=eq.${threadId}` : undefined;
    const channel = supabase
      .channel(`thread-state-${threadId ?? "home"}-${subscriberId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "work_threads",
          ...(threadId ? { filter: `id=eq.${threadId}` } : {}),
        },
        scheduleRefresh,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "thread_topics",
          ...(threadFilter ? { filter: threadFilter } : {}),
        },
        scheduleRefresh,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "action_runs",
          ...(threadFilter ? { filter: threadFilter } : {}),
        },
        scheduleRefresh,
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "thread_domain_events",
          ...(threadFilter ? { filter: threadFilter } : {}),
        },
        scheduleRefresh,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "thread_wait_conditions",
          ...(threadFilter ? { filter: threadFilter } : {}),
        },
        scheduleRefresh,
      )
      .subscribe();

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [enabled, threadId, subscriberId]);
};
